/**
 * LRU preview cache (P1-G5) — caps storage at MAX_PREVIEW_CACHE entries.
 *
 * Preview render output is heavyweight (~50KB × 12 files per draft). The mock
 * service stores them in localStorage so navigation back to the Preview step
 * doesn't re-run Jinja. On QuotaExceededError we evict oldest entries; if
 * eviction doesn't free enough we surface a toast and drop the new write.
 */

const MAX_PREVIEW_CACHE = 5;
const STORAGE_KEY = 'studio-mock-preview-lru';

interface LruEntry {
  draft_id: string;
  last_accessed: number; // unix-seconds
}

function readIndex(): LruEntry[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeIndex(entries: LruEntry[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
}

function previewKey(draftId: string): string {
  return `studio-mock-preview-${draftId}`;
}

export function getPreview(draftId: string): Record<string, string> | null {
  try {
    const raw = localStorage.getItem(previewKey(draftId));
    if (!raw) return null;

    // Touch LRU.
    const index = readIndex();
    const entry = index.find((e) => e.draft_id === draftId);
    if (entry) {
      entry.last_accessed = Math.floor(Date.now() / 1000);
      writeIndex(index);
    }

    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export interface PutPreviewResult {
  ok: boolean;
  evicted: string[];
  storage_full?: boolean;
}

export function putPreview(draftId: string, files: Record<string, string>): PutPreviewResult {
  const evicted: string[] = [];
  let index = readIndex();
  const now = Math.floor(Date.now() / 1000);

  // Evict oldest entries beyond cap.
  const others = index.filter((e) => e.draft_id !== draftId);
  if (others.length >= MAX_PREVIEW_CACHE) {
    const toEvict = [...others]
      .sort((a, b) => a.last_accessed - b.last_accessed)
      .slice(0, others.length - (MAX_PREVIEW_CACHE - 1));
    for (const e of toEvict) {
      try {
        localStorage.removeItem(previewKey(e.draft_id));
        evicted.push(e.draft_id);
      } catch {
        /* ignore */
      }
    }
    index = index.filter((e) => !toEvict.some((ev) => ev.draft_id === e.draft_id));
  }

  // Upsert LRU entry.
  const existing = index.find((e) => e.draft_id === draftId);
  if (existing) {
    existing.last_accessed = now;
  } else {
    index.push({ draft_id: draftId, last_accessed: now });
  }

  // Try to write.
  try {
    localStorage.setItem(previewKey(draftId), JSON.stringify(files));
    writeIndex(index);
    return { ok: true, evicted };
  } catch (err) {
    // QuotaExceededError after eviction attempt. Drop oldest and try again ONCE.
    if (others.length > 0) {
      const oldest = [...others].sort((a, b) => a.last_accessed - b.last_accessed)[0];
      try {
        localStorage.removeItem(previewKey(oldest.draft_id));
        evicted.push(oldest.draft_id);
        index = index.filter((e) => e.draft_id !== oldest.draft_id);
        localStorage.setItem(previewKey(draftId), JSON.stringify(files));
        writeIndex(index);
        return { ok: true, evicted };
      } catch {
        return { ok: false, evicted, storage_full: true };
      }
    }
    return { ok: false, evicted, storage_full: true };
  }
}

export function invalidatePreview(draftId: string): void {
  try {
    localStorage.removeItem(previewKey(draftId));
    const index = readIndex().filter((e) => e.draft_id !== draftId);
    writeIndex(index);
  } catch {
    /* ignore */
  }
}
